import { useRequest } from 'ahooks'
import { startAuthentication } from '@simplewebauthn/browser'
import toast from 'react-hot-toast'
import { isError } from 'lodash-es'

import { asyncSuccessResolver } from '$app/utilities'
import {
  errorMessageResolver,
  errorStatusResolver,
  successResolver
} from '$services/api/common'

import { FactorHookOptions } from './types'

type UsePasskeyOptions = FactorHookOptions

export const usePasskey = ({
  apiInstance: {
    mfaPasskeyAuthenticationOption,
    mfaPasskeyAuthenticationVerify
  },
  onVerified
}: UsePasskeyOptions) => {
  const onApiError = (err: unknown) => {
    if (errorStatusResolver(err) === 401) {
      reset()
    }
  }

  const doAuthenticate = async () => {
    const optionsRes = await mfaPasskeyAuthenticationOption()
    const { data: options } = successResolver(optionsRes)

    const payloads = await startAuthentication({ optionsJSON: options })

    return { payloads, options }
  }

  const doVerify = async (res: Awaited<ReturnType<typeof doAuthenticate>>) => {
    if (!res) return

    const {
      payloads,
      options: { reference_code }
    } = res

    toast.promise(
      verifyReq.runAsync({
        reference_code,
        payloads
      }),
      {
        loading: 'Verifying..',
        error: errorMessageResolver,
        success: 'Verified'
      }
    )
  }

  const authenticateReq = useRequest(doAuthenticate, {
    manual: true,
    onError: onApiError,
    onSuccess: doVerify
  })

  const verifyReq = useRequest(
    asyncSuccessResolver(mfaPasskeyAuthenticationVerify, successResolver),
    {
      manual: true,
      onError: onApiError,
      onSuccess: ({ data: { mfa_token } }) => onVerified(mfa_token)
    }
  )

  const requests = [authenticateReq, verifyReq]

  const isAnyLoading = requests.map(({ loading }) => loading).includes(true)

  const authenticate = async () => {
    toast.promise(authenticateReq.runAsync(), {
      loading: 'Authenticating..',
      error: e => {
        if (isError(e) && e.name === 'NotAllowedError') {
          return 'Authentication timed out or was cancelled'
        }

        return errorMessageResolver(e, 'Failed to authenticate')
      },
      success: 'Authenticated'
    })
  }

  const reset = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })
  }

  return {
    isAnyLoading,
    reset,
    authenticate
  }
}

export default usePasskey
